import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { ListReportPanoramasWithConditionsResp } from '@/shared/api/protocol-ts/api/core/svc_report_pb';
import { Condition } from '@/shared/api/protocol-ts/model/dto_report_condition_pb';
import {
  DefaultInitialState,
  LoadingStateType,
  SliceName,
} from '@/shared/config';

import type { ConditionIDGroup, ReportConditionsSnapshot } from '../config';
import { sortConditionsByGroupOrder } from '../lib';

export const conditionAdapter = createEntityAdapter<Condition>({
  selectId: (condition: Condition) => condition.ID,
  sortComparer: sortConditionsByGroupOrder,
});

type ConditionInitialState = DefaultInitialState & {
  teethGroup: ConditionIDGroup;
  maxFaxGroup: string[];
  hoveredConditionIDs: string[];
  hoveredNonDentalConditionIDs: string[];
  reportConditionsSnapshot: ReportConditionsSnapshot;
};

const makeConditionInitialState = (): ConditionInitialState => ({
  loading: 'idle',
  teethGroup: {},
  maxFaxGroup: [],
  hoveredConditionIDs: [],
  hoveredNonDentalConditionIDs: [],
  reportConditionsSnapshot: {
    reportID: '',
    loading: 'idle',
  },
});

const initialState = conditionAdapter.getInitialState(
  makeConditionInitialState(),
);

type ConditionState = typeof initialState;

const conditionSlice = createSlice({
  name: SliceName.condition,
  initialState: conditionAdapter.getInitialState(makeConditionInitialState()),
  reducers: {
    addOne: (state, action: PayloadAction<Condition>) =>
      conditionAdapter.addOne(state as ConditionState, action.payload),
    addMany: (state, action: PayloadAction<Condition[]>) =>
      conditionAdapter.addMany(state as ConditionState, action.payload),
    setNewestOne: (state, action: PayloadAction<Condition>) => {
      if (action.payload.Deleted?.Deleted) {
        conditionAdapter.removeOne(state as ConditionState, action.payload.ID);
      }

      const currentCondition = state.entities[action.payload.ID];

      const payloadToothID =
        action.payload.Attribution.case === 'Tooth'
          ? action.payload.Attribution.value.ToothID
          : '';

      const currentToothID =
        currentCondition && currentCondition.Attribution.case === 'Tooth'
          ? currentCondition.Attribution.value.ToothID
          : '';

      // NOTE: this is necessary for the correct handling of the teethGroup update when changing the tooth number
      if (payloadToothID !== currentToothID) {
        if (payloadToothID in state.teethGroup) {
          state.teethGroup[payloadToothID].push(action.payload.ID);
        } else {
          state.teethGroup[payloadToothID] = [action.payload.ID];
        }

        if (currentToothID && state.teethGroup[currentToothID]) {
          state.teethGroup[currentToothID] = state.teethGroup[
            currentToothID
          ].filter((conditionID) => conditionID !== action.payload.ID);
        }
      }

      const currentRevisionNumber = currentCondition?.Revision?.Number ?? 0;
      const payloadRevisionNumber = action.payload.Revision?.Number ?? 0;

      if (payloadRevisionNumber > currentRevisionNumber) {
        conditionAdapter.setOne(state as ConditionState, action.payload);
      }
    },
    setNewestMany: (state, action: PayloadAction<Condition[]>) => {
      const updatedConditionsFromPayload = action.payload.filter(
        (updateCondition) => {
          const currentRevisionNumber =
            state.entities[updateCondition.ID]?.Revision?.Number ?? 0;
          const payloadRevisionNumber = updateCondition.Revision?.Number ?? 0;

          return payloadRevisionNumber > currentRevisionNumber;
        },
      );

      // NOTE: this is necessary for the correct handling of the teethGroup update when changing the tooth number
      updatedConditionsFromPayload.forEach((updatedCondition) => {
        const currentCondition = state.entities[updatedCondition.ID];

        const payloadToothID =
          updatedCondition.Attribution.case === 'Tooth'
            ? updatedCondition.Attribution.value.ToothID
            : '';

        const currentToothID =
          currentCondition && currentCondition.Attribution.case === 'Tooth'
            ? currentCondition.Attribution.value.ToothID
            : '';

        if (payloadToothID !== currentToothID) {
          if (payloadToothID in state.teethGroup) {
            state.teethGroup[payloadToothID].push(updatedCondition.ID);
          } else {
            state.teethGroup[payloadToothID] = [updatedCondition.ID];
          }

          if (currentToothID && state.teethGroup[currentToothID]) {
            state.teethGroup[currentToothID] = state.teethGroup[
              currentToothID
            ].filter((conditionID) => conditionID !== updatedCondition.ID);
          }
        }
      });

      conditionAdapter.setMany(
        state as ConditionState,
        updatedConditionsFromPayload,
      );
    },
    setMany: (state, action: PayloadAction<Condition[]>) =>
      conditionAdapter.setMany(state as ConditionState, action.payload),
    removeOne: (state, action: PayloadAction<string>) =>
      conditionAdapter.removeOne(state as ConditionState, action.payload),
    removeAll: (state) => conditionAdapter.removeAll(state as ConditionState),
    setLoading: (state, action: PayloadAction<LoadingStateType>) => {
      state.loading = action.payload;
    },
    initializeTeethGroup: (state, action: PayloadAction<ConditionIDGroup>) => {
      state.teethGroup = action.payload;
    },
    initializeMaxFaxGroup: (state, action: PayloadAction<string[]>) => {
      state.maxFaxGroup = action.payload;
    },
    addTeethGroup: (state, action: PayloadAction<Condition[]>) => {
      const teethGroup = state.teethGroup;

      action.payload.forEach((condition) => {
        const conditionToothID =
          condition.Attribution.case === 'Tooth'
            ? condition.Attribution.value.ToothID
            : '';

        if (
          conditionToothID in teethGroup &&
          !teethGroup[conditionToothID].includes(condition.ID)
        ) {
          teethGroup[conditionToothID].push(condition.ID);
        }
      });
    },
    setHoveredConditionIDs: (state, action: PayloadAction<string[]>) => {
      state.hoveredConditionIDs = action.payload;
    },
    setHoveredNonDentalConditionIDs: (
      state,
      { payload }: PayloadAction<string[]>,
    ) => {
      state.hoveredNonDentalConditionIDs = payload;
    },
    startReportConditionsSnapshot: (
      state,
      action: PayloadAction<{ reportID: string }>,
    ) => {
      state.reportConditionsSnapshot = {
        reportID: action.payload.reportID,
        loading: 'pending',
      };
    },
    completeReportConditionsSnapshot: (
      state,
      action: PayloadAction<{ reportID: string }>,
    ) => {
      if (state.reportConditionsSnapshot.reportID === action.payload.reportID) {
        state.reportConditionsSnapshot.loading = 'succeeded';
      }
    },
    failReportConditionsSnapshot: (
      state,
      action: PayloadAction<{ reportID: string }>,
    ) => {
      if (state.reportConditionsSnapshot.reportID === action.payload.reportID) {
        state.reportConditionsSnapshot.loading = 'failed';
      }
    },
    reset: () => conditionAdapter.getInitialState(makeConditionInitialState()),
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (
        action,
      ): action is PayloadAction<ListReportPanoramasWithConditionsResp> =>
        action.type ===
        `${SliceName.reports}/listReportPanoramasWithConditions/fulfilled`,
      (state, action) =>
        conditionAdapter.setMany(
          state as ConditionState,
          action.payload.Items.flatMap((item) => item.Conditions),
        ),
    );
    builder.addMatcher(
      (action): action is PayloadAction<{ reportIDs: string[] }> =>
        action.type === `${SliceName.reports}/clearReportPanoramas`,
      (state, action) =>
        conditionAdapter.removeMany(
          state as ConditionState,
          Object.keys(state.entities).filter((id) =>
            action.payload.reportIDs.includes(
              state.entities[id]?.ReportID ?? '',
            ),
          ),
        ),
    );
  },
});

export const { actions } = conditionSlice;

export default conditionSlice.reducer;
